import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

function DiscountCalculator() {
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setResult(null);
    setShowFunFact(false);
    setError("");
  };

  const calculate = () => {
    const p = parseFloat(price);
    const d = parseFloat(discount);
    if (isNaN(p) || isNaN(d) || p < 0) {
      setError("Please enter a price and a discount percentage.");
      setResult(null);
      setShowFunFact(false);
      return;
    }
    if (d < 0 || d > 100) {
      setError("Discount must be between 0 and 100 percent.");
      setResult(null);
      setShowFunFact(false);
      return;
    }

    const saved = (p * d) / 100;
    setError("");
    setResult({
      saved: saved.toFixed(2),
      final: (p - saved).toFixed(2),
    });

    const funFactsList = [
      "Retailers often price items at .99 because shoppers read prices left to right.",
      "\"50% off\" and \"buy one get one free\" cost the store the same — but shoppers prefer BOGO.",
      "Black Friday discounts average around 30–40% off list price.",
      "Anchoring: seeing the original price first makes the discount feel bigger.",
      "Stacked discounts multiply, they don't add — 20% off then 10% off is 28% off, not 30%.",
    ];
    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="Discount Calculator"
      metaDescription="Free discount calculator: enter the original price and discount percentage to see the final sale price and exactly how much you save."
      intro={
        <>
          Enter the original price and the <strong>discount percentage</strong>{" "}
          to see the final price and how much you save.
        </>
      }
      seoContent={
        <>
          <h2>How the discount is calculated</h2>
          <p>
            The amount you save is the original price multiplied by the
            discount percentage divided by 100. The sale price is the original
            price minus that saving. For example, a $60 item at 25% off saves
            you $15, so you pay $45.
          </p>
          <h2>Watch out for stacked discounts</h2>
          <p>
            When a store applies an extra discount on top of an existing sale
            ("additional 10% off clearance"), the second discount applies to
            the already-reduced price, not the original. A 20% discount
            followed by 10% off gives a total of 28% off — not 30%. Run the
            calculator twice, feeding the first result into the second
            calculation, to get the true final price.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>How do I calculate the percentage off from two prices?</h3>
          <p>
            Subtract the sale price from the original price, divide by the
            original price, and multiply by 100. For example, an item reduced
            from $80 to $60 is (80 − 60) ÷ 80 × 100 = 25% off.
          </p>
          <h3>Is tax applied before or after the discount?</h3>
          <p>
            In most places the discount is applied first and sales tax is then
            charged on the reduced price, so you pay tax only on what you
            actually spend. This calculator shows the pre-tax sale price.
          </p>
          <h3>Do two stacked discounts add up?</h3>
          <p>
            No. A 20% discount followed by another 10% is not 30% off — the
            second cut applies to the already-reduced price, giving 28% off
            overall.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Original Price</span>
          <input type="number" value={price} onChange={onInput(setPrice)} placeholder="e.g. 60" />
        </label>
        <label className="field">
          <span>Discount (%)</span>
          <input type="number" value={discount} onChange={onInput(setDiscount)} placeholder="e.g. 25" />
        </label>
      </div>

      <button className="btn" onClick={calculate}>Calculate Discount</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <>
          <div className="result">
            You save <strong>{result.saved}</strong> — final price:{" "}
            <strong>{result.final}</strong>
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default DiscountCalculator;
